// INSIGHTS-DRE: gera o one-pager executivo A4 como HTML autocontido (CSS + SVG
// inline) e abre em janela nova pra impressão → "Salvar como PDF". Sem lib de
// PDF (nenhuma dep nova a 2 dias da apresentação); print nativo = PDF vetorial.
import {
  DreResp, receitaLiquida, kpisComparados, composicao, receitaPorArea, narrativa, reinvestimento,
} from './insightsCalc';

const brl = (v: number) =>
  'R$ ' + Math.abs(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const brlK = (v: number) => (Math.abs(v) >= 1000 ? (v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 0 }) + 'k' : v.toFixed(0));
const esc = (s: string) => s.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));

// Barra comparativa horizontal A×B (SVG) — pra composição e áreas.
function barrasAB(itens: Array<{ rotulo: string; a: number; b: number }>, corA: string, corB: string): string {
  const max = Math.max(1, ...itens.flatMap(i => [i.a, i.b]));
  const W = 240, rowH = 34;
  const linhas = itens.map((it, idx) => {
    const y = idx * rowH;
    const wA = (it.a / max) * W, wB = (it.b / max) * W;
    return `
      <text x="0" y="${y + 10}" class="lbl">${esc(it.rotulo)}</text>
      <rect x="120" y="${y + 2}"  width="${wA.toFixed(1)}" height="9" rx="2" fill="${corA}"/>
      <rect x="120" y="${y + 13}" width="${wB.toFixed(1)}" height="9" rx="2" fill="${corB}"/>
      <text x="${(120 + wB + 4).toFixed(1)}" y="${y + 21}" class="val">${brlK(it.b)}</text>`;
  }).join('');
  return `<svg width="${120 + W + 40}" height="${itens.length * rowH}" xmlns="http://www.w3.org/2000/svg">${linhas}</svg>`;
}

export function gerarOnePagerHTML(
  a: DreResp, b: DreResp, rotA: string, rotB: string, logoDataUrl?: string
): string {
  const rlA = receitaLiquida(a), rlB = receitaLiquida(b);
  const kpis = kpisComparados(a, b);
  const comp = composicao(a, b);
  const areas = receitaPorArea(a, b);
  const frases = narrativa(a, b, rotA, rotB);
  const hoje = new Date().toLocaleDateString('pt-BR');

  const kpiCards = kpis.map(k => {
    const up = k.deltaAbs >= 0;
    const cor = k.chave === 'receita' ? (up ? '#16a34a' : '#dc2626')
              : (up ? '#16a34a' : '#dc2626'); // pra despesa seria invertido; aqui todos são "quanto maior melhor"
    const seta = up ? '▲' : '▼';
    const dpct = k.deltaPct === null ? '—' : `${up ? '+' : ''}${k.deltaPct.toFixed(1)}%`;
    const marg = k.margemB === null ? '' : `<div class="kmarg">margem ${k.margemB.toFixed(0)}%${k.refMercado ? ` · mercado ${k.refMercado[0]}–${k.refMercado[1]}%` : ''}</div>`;
    return `
      <div class="kpi">
        <div class="ktit">${esc(k.titulo)}</div>
        <div class="kval">${brl(k.valorB)}</div>
        <div class="kdelta" style="color:${cor}">${seta} ${dpct} <span class="kvs">vs ${esc(rotA)}</span></div>
        ${marg}
      </div>`;
  }).join('');

  const compBars = barrasAB(comp.map(c => ({ rotulo: c.grupo, a: c.valorA, b: c.valorB })), '#94a3b8', '#0ea5e9');
  const areaBars = barrasAB(areas.map(ar => ({ rotulo: ar.rotulo, a: ar.valorA, b: ar.valorB })), '#94a3b8', '#2563eb');

  const semRec = brl(rlA + rlB), semEbitda = brl(a.resultadoOp + b.resultadoOp), semLucro = brl(a.lucroLiq + b.lucroLiq);
  const reinv = reinvestimento(a, b);

  return `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
<title>Hydrodata — Resultados do Semestre</title>
<style>
  @page { size: A4 portrait; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, 'Segoe UI', Roboto, Arial, sans-serif; color: #0f172a; margin: 0; font-size: 11px; }
  .logo-row { margin-bottom: 8px; }
  .logo-row img { height: 30px; display: block; }
  .hdr { display: flex; align-items: flex-end; justify-content: space-between; border-bottom: 2px solid #0ea5e9; padding-bottom: 8px; margin-bottom: 12px; }
  .hdr h1 { font-size: 17px; margin: 0; }
  .hdr .sub { color: #64748b; font-size: 11px; margin-top: 2px; }
  .hdr .meta { text-align: right; color: #64748b; font-size: 10px; white-space: nowrap; }
  .strip { display: flex; gap: 10px; margin-bottom: 12px; }
  .strip .s { flex: 1; background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 8px; padding: 8px 10px; }
  .strip .s .l { color: #0369a1; font-size: 9px; text-transform: uppercase; letter-spacing: .3px; }
  .strip .s .v { font-size: 14px; font-weight: 700; margin-top: 2px; white-space: nowrap; }
  .strip .s.g { background: #f0fdf4; border-color: #bbf7d0; }
  .strip .s.g .l { color: #15803d; }
  .kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 14px; }
  .kpi { border: 1px solid #e2e8f0; border-radius: 8px; padding: 9px 10px; }
  .kpi .ktit { color: #64748b; font-size: 10px; text-transform: uppercase; letter-spacing: .3px; }
  .kpi .kval { font-size: 17px; font-weight: 700; margin: 3px 0; }
  .kpi .kdelta { font-size: 11px; font-weight: 600; }
  .kpi .kvs { color: #94a3b8; font-weight: 400; }
  .kpi .kmarg { color: #64748b; font-size: 9.5px; margin-top: 3px; }
  .cols { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin-bottom: 12px; }
  .card h2 { font-size: 12px; margin: 0 0 6px; color: #0f172a; }
  .card .leg { color: #64748b; font-size: 9.5px; margin-bottom: 4px; }
  .lbl { font-size: 9.5px; fill: #334155; }
  .val { font-size: 9px; fill: #0f172a; font-weight: 600; }
  .narr { background: #fafafa; border-left: 3px solid #0ea5e9; padding: 8px 12px; border-radius: 4px; }
  .narr li { margin: 3px 0; }
  .foot { color: #94a3b8; font-size: 9px; margin-top: 12px; border-top: 1px solid #e2e8f0; padding-top: 6px; }
  .swatch { display: inline-block; width: 9px; height: 9px; border-radius: 2px; vertical-align: middle; margin-right: 3px; }
</style></head><body>
  ${logoDataUrl ? `<div class="logo-row"><img src="${logoDataUrl}" alt="Hydrodata Engenharia"></div>` : ''}
  <div class="hdr">
    <div>
      <h1>Resultados do Semestre${logoDataUrl ? '' : ' — Hydrodata Engenharia'}</h1>
      <div class="sub">Demonstração de Resultado · comparativo ${esc(rotA)} × ${esc(rotB)}</div>
    </div>
    <div class="meta">Emitido em ${hoje}</div>
  </div>

  <div class="strip">
    <div class="s"><div class="l">Receita Líquida (semestre)</div><div class="v">${semRec}</div></div>
    <div class="s"><div class="l">EBITDA (semestre)</div><div class="v">${semEbitda}</div></div>
    <div class="s"><div class="l">Lucro Líquido (semestre)</div><div class="v">${semLucro}</div></div>
    <div class="s g"><div class="l">Reinvestido na empresa${reinv.pctReinvestido !== null ? ` (${reinv.pctReinvestido.toFixed(0)}% do lucro)` : ''}</div><div class="v">${brl(reinv.investido)}</div></div>
    <div class="s g"><div class="l">Lucro Final</div><div class="v">${brl(reinv.lucroFinal)}</div></div>
  </div>

  <div class="kpis">${kpiCards}</div>

  <div class="cols">
    <div class="card">
      <h2>Composição de custo/despesa</h2>
      <div class="leg"><span class="swatch" style="background:#94a3b8"></span>${esc(rotA)} &nbsp; <span class="swatch" style="background:#0ea5e9"></span>${esc(rotB)}</div>
      ${compBars}
    </div>
    <div class="card">
      <h2>Receita por área</h2>
      <div class="leg"><span class="swatch" style="background:#94a3b8"></span>${esc(rotA)} &nbsp; <span class="swatch" style="background:#2563eb"></span>${esc(rotB)}</div>
      ${areaBars}
    </div>
  </div>

  <div class="card">
    <h2>Leitura do semestre</h2>
    <ul class="narr">${frases.map(f => `<li>${esc(f)}</li>`).join('')}</ul>
  </div>

  <div class="foot">
    EBITDA = resultado operacional (sem depreciação lançada na DRE). Impostos em modo estimativa fiscal (Lucro Presumido).
    Lucro Final = lucro líquido − investimentos do período (equipamentos, software e estrutura).
    Fonte: sistema DRE Hydrodata · gerado automaticamente.
  </div>
</body></html>`;
}

// Abre a janela de impressão com o one-pager pronto.
export function imprimirOnePager(html: string) {
  const w = window.open('', '_blank', 'width=900,height=1200');
  if (!w) { alert('Permita pop-ups para exportar o PDF.'); return; }
  w.document.write(html);
  w.document.close();
  // aguarda render (imagens/fontes) antes de abrir o diálogo de impressão
  w.onload = () => { setTimeout(() => { w.focus(); w.print(); }, 350); };
}
